import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useReducer
} from 'react';
import useGlobalVariables from '../../../hooks/useGlobalVariables';
import useTranslations from '../../../hooks/useTranslations';
import fetchAPI from '../../../utils/fetchAPI';
import generateId from '../../../utils/generateId';
import AdminQueryBuilderView from '../view/AdminQueryBuilderView';

// [{"field":"'assigneeId'","op":"=","value":"852e2de8-2410-49e2-931e-6d59e481a05b"}]

const SubmitterQueryBuilderReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const initialState = {
  internOp: 'or',
  admins: {}
};

const AdminQueryBuilderEngine = ({
  query = [],
  update = () => {},
  sentencePrefix,
  form = {}
}) => {
  const [data, dispatch] = useReducer(
    SubmitterQueryBuilderReducer,
    initialState
  );
  const [inDeleteSequence, setInDeleteSequence] = useState(null);
  const [hoverOn, setHoverOn] = useState(null);

  const t = useTranslations();
  const { setIsLoading } = useGlobalVariables();
  // const [placeHolder, setPlaceHolder] = useState('Choose an admin');
  // const [adminUserId, setAdminUserId] = useState(null);
  const skipUpdate = useRef(true);

  useEffect(() => {
    if (skipUpdate.current) {
      skipUpdate.current = false;
      return;
    }

    const query2Export = Object.values(data.admins).map(a => ({
      field: "'assigneeId'",
      op: '=',
      value: a.id,
      internOp: data.internOp
    }));

    update(query2Export);
  }, [data]);

  useEffect(() => {
    if (!!!query.length) {
      return;
    }

    const adminsPromises = query.map(qBlock => {
      const userId = qBlock.value;
      if (!!!userId) {
        return null;
      }

      return new Promise((res, rej) => {
        fetchAPI(`/users/v1/users/${userId}`).then(r => {
          const info = r.info;
          if (!!!info) {
            return;
          }

          const admin = {
            id: userId,
            name: `${info.firstName} ${info.lastName}`
          };

          res(admin);
        });
      });
    });

    setIsLoading(true);
    Promise.all(adminsPromises)
      .then(adminsArr => {
        const admins = {};
        adminsArr.forEach(a => (admins[`id-${generateId()}`] = !!a ? a : {}));
        skipUpdate.current = true;

        dispatch({
          type: 'update',
          payload: {
            admins
          }
        });
      })
      .finally(() => setIsLoading(false));
  }, []);

  const getFormAdminsOptions = useCallback(() => {
    return fetchAPI(
      `/forms/v1/forms/${form.id}/collaborate?limit=500&addUserInfo=true`
    ).then(response => {
      const admins = response.results;
      return admins.map(a => ({
        value: a.user_id,
        text: `${a.info.firstName} ${a.info.lastName}`,
        'data-testid': `select-admin-${a.user_id}`
      }));
    });
  }, [form]);

  const removeCondition = useCallback(
    id => {
      const adminsCpy = JSON.parse(JSON.stringify(data.admins));
      delete adminsCpy[id];
      dispatch({ type: 'update', payload: { admins: adminsCpy } });
    },
    [data]
  );
  const setAdminUserId = useCallback(
    (id, adminId) => {
      const adminsCpy = JSON.parse(JSON.stringify(data.admins));
      adminsCpy[id] = { id: adminId };
      dispatch({ type: 'update', payload: { admins: adminsCpy } });
    },
    [data]
  );

  const addCondition = useCallback(() => {
    const adminsCpy = JSON.parse(JSON.stringify(data.admins));
    adminsCpy[`id-${generateId()}`] = {};
    skipUpdate.current = true;
    dispatch({ type: 'update', payload: { admins: adminsCpy } });
  }, [data]);

  const onRemoveConditionClicked = useCallback(
    id => {
      if (Object.keys(data.admins).length === 1) {
        // we're deleting the last (sub-)condition - we want to use the
        // condition delete and not sub-condition delete.
        // We trigger it by sending empt query
        update([]);
        return;
      }
      setInDeleteSequence(id);
    },
    [data, update]
  );

  const viewProps = {
    sentencePrefix: sentencePrefix || t('logic.when_admin'),
    getFormAdminsOptions,
    removeCondition,
    addCondition,
    setAdminUserId,
    onRemoveConditionClicked,
    ...data,
    hoverOn,
    setHoverOn,
    inDeleteSequence
  };

  return <AdminQueryBuilderView {...viewProps} />;
};

export default AdminQueryBuilderEngine;
